/**
 * Last-event-wins derivation for the daily check-in's granular fields
 * (`checkin.mood_set` / `checkin.energy_set` / `checkin.note_set`) --
 * `DailyPageView.mood` / `.energy` / `.note` (domain-api.ts).
 *
 * ADR-0002's original vocabulary table already specified the rule ("Last
 * event (by eventId order) wins for the day") but no reader ever implemented
 * it (ADR-0002 addendum, 2026-07-03: "there is no last-event-wins reader for
 * checkin.mood_set/energy_set/note_set anywhere in the codebase today").
 * This module is that reader -- the fourth application in this codebase of
 * the same "iterate forward in canonical order, let a later match overwrite
 * an earlier one in a Map" pattern already used by
 * `computeActionCompletionStatus` (objectives/state.ts) and
 * `computeBuildLogStatuses` (habits/logging.ts), generalized to three
 * independent per-day fields computed in one pass over the log instead of
 * inventing three separate single-field functions.
 */

import type { CompassEvent, EventId, IsoDate, Rating } from '../../contracts';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';

export interface DailyFields {
  readonly mood: Rating | null;
  readonly energy: Rating | null;
  readonly note: string;
  /**
   * The winning (last, surviving) checkin.mood_set/energy_set event's ID,
   * or null when no such event exists for this date -- mirrors
   * `DueActionView.entryEventId`/`DueHabitView.entryEventId`'s established
   * "expose the event ID for one-tap revert" convention (ME-4/AC-P9: a
   * mood/energy tap must be revertible via the existing generic
   * `revertLogEntry`, which needs a concrete eventId to target).
   */
  readonly moodEventId: EventId | null;
  readonly energyEventId: EventId | null;
}

const NO_FIELDS: DailyFields = {
  mood: null,
  energy: null,
  note: '',
  moodEventId: null,
  energyEventId: null,
};

/**
 * The mood/energy/note values in effect for one `date`: the last (canonical
 * log order) surviving `checkin.mood_set`/`checkin.energy_set`/
 * `checkin.note_set` event with `occurredOn === date`, per field
 * independently -- a mood tap and an energy tap on the same day do not
 * interact, matching the events' own doc comments ("Last event in log order
 * wins for the day", scoped per event TYPE, not per day as a whole).
 * `note` defaults to `''` (matches `NoteSetEvent`'s "empty string clears"
 * semantics -- absence and an explicit clear are indistinguishable by
 * design, exactly like every other field here defaulting to "as if never
 * set"); `mood`/`energy` default to `null`.
 */
export function computeDailyFields(
  events: readonly CompassEvent[],
  date: IsoDate | string,
): DailyFields {
  const active = excludingReverted(events);
  const relevant = eventsOfTypes(active, [
    'checkin.mood_set',
    'checkin.energy_set',
    'checkin.note_set',
  ]).filter((event) => event.occurredOn === date);

  if (relevant.length === 0) return NO_FIELDS;

  let mood: Rating | null = null;
  let energy: Rating | null = null;
  let note = '';
  let moodEventId: EventId | null = null;
  let energyEventId: EventId | null = null;

  // `relevant` preserves canonical (eventId) order per eventsOfTypes's own
  // contract, so a later match for the same field simply overwrites an
  // earlier one -- no separate per-field grouping pass needed.
  for (const event of relevant) {
    if (event.type === 'checkin.mood_set') {
      mood = event.mood;
      moodEventId = event.eventId;
    } else if (event.type === 'checkin.energy_set') {
      energy = event.energy;
      energyEventId = event.eventId;
    } else {
      note = event.note;
    }
  }

  return { mood, energy, note, moodEventId, energyEventId };
}
